import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ArrowDown, ArrowUp, ArrowUpDown, CircleCheck, CirclePause, CircleSlash, Clock, Info, RefreshCw, TriangleAlert,
} from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import { Rolling72hGovernanceCard } from '../components/meta/Rolling72hGovernanceCard';
import {
  fetchMetaDashboard, MetaDashboardKpis, MetaDashboardResponse, MetaDashboardRow, MetaGeo, MetaLevel,
} from '../services/metaAdsApi';

// SimplyLoan brand: pine green for structure, warm orange only on the primary action, soft mint as wash.
const PINE = '#0F3F34';
const ORANGE = '#F07824';
const MINT = '#F4F8F6';
// Status hues are reserved for status and always ship with an icon and a label.
const STATUS = { good: '#0ca30c', warning: '#fab219', critical: '#d03b3b', neutral: '#8a8984' };
const TZ = 'Europe/Copenhagen';

type Preset = '7d' | '30d' | '90d' | 'custom';
type SortKey = 'name' | 'spend_dkk' | 'leads' | 'cpl_dkk' | 'rev_d0' | 'immediate_roas' | 'rev_d30' | 'ltv30_roas' | 'rev_pending';

const PRESET_DAYS: Record<Exclude<Preset, 'custom'>, number> = { '7d': 7, '30d': 30, '90d': 90 };
const GEOS: { id: MetaGeo; label: string }[] = [
  { id: 'all', label: 'All' }, { id: 'se', label: 'SE' }, { id: 'no', label: 'NO' }, { id: 'fi', label: 'FI' },
];
const LEVELS: { id: MetaLevel; label: string }[] = [
  { id: 'campaign', label: 'Campaign' }, { id: 'adset', label: 'Ad set' }, { id: 'ad', label: 'Ad' },
];

const dkkFormat = new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 });
const intFormat = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 });
const pctFormat = new Intl.NumberFormat('da-DK', { style: 'percent', maximumFractionDigits: 1 });

const money = (v: number | null | undefined) => (v == null ? '–' : dkkFormat.format(v));
const count = (v: number | null | undefined) => (v == null ? '–' : intFormat.format(v));
const roas = (v: number | null | undefined) =>
  v == null ? '–' : `${v.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;

const isoDay = (d: Date) => d.toLocaleDateString('sv-SE', { timeZone: TZ });

function presetRange(preset: Exclude<Preset, 'custom'>) {
  const to = new Date();
  const from = new Date(to.getTime() - (PRESET_DAYS[preset] - 1) * 86_400_000);
  return { from: isoDay(from), to: isoDay(to) };
}

function describeStatus(status: string | null) {
  switch (status) {
    case 'ACTIVE':
      return { label: 'Active', color: STATUS.good, Icon: CircleCheck };
    case 'PAUSED':
    case 'CAMPAIGN_PAUSED':
    case 'ADSET_PAUSED':
      return { label: 'Paused', color: STATUS.neutral, Icon: CirclePause };
    case 'IN_PROCESS':
    case 'PENDING_REVIEW':
    case 'PREAPPROVED':
      return { label: 'In review', color: STATUS.warning, Icon: Clock };
    case 'DISAPPROVED':
      return { label: 'Disapproved', color: STATUS.critical, Icon: TriangleAlert };
    case 'WITH_ISSUES':
    case 'PENDING_BILLING_INFO':
      return { label: 'Needs attention', color: STATUS.critical, Icon: TriangleAlert };
    case 'ARCHIVED':
    case 'DELETED':
      return { label: 'Archived', color: STATUS.neutral, Icon: CircleSlash };
    default:
      return null;
  }
}

function StatusPill({ status }: { status: string | null }) {
  const s = describeStatus(status);
  if (!s) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600">
      <s.Icon size={12} color={s.color} aria-hidden="true" />
      {s.label}
    </span>
  );
}

function KpiTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
    </div>
  );
}

function Segmented<T extends string>({ options, value, onChange, label }: {
  options: { id: T; label: string; short?: string }[]; value: T; onChange: (v: T) => void; label: string;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          aria-pressed={value === o.id}
          aria-label={o.label}
          className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === o.id ? 'text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}
          style={value === o.id ? { backgroundColor: PINE } : undefined}
        >
          {o.short ? (
            <>
              <span className="sm:hidden">{o.short}</span>
              <span className="hidden sm:inline">{o.label}</span>
            </>
          ) : o.label}
        </button>
      ))}
    </div>
  );
}

function kpiTiles(k: MetaDashboardKpis) {
  // Row 1: what the money bought; row 2: what it earned.
  return [
    { label: 'Meta spend', value: money(k.spend_dkk), sub: k.unmapped_spend_dkk ? `${money(k.unmapped_spend_dkk)} not mapped to a market` : 'Insights, converted to DKK' },
    { label: 'Leads', value: count(k.leads), sub: `${count(k.lead_ad_leads)} Instant Form · Meta reports ${count(k.meta_reported_leads)}` },
    { label: 'Cost per lead', value: money(k.cpl_dkk), sub: 'Spend ÷ our leads' },
    { label: 'Blended ROAS', value: roas(k.blended_roas), sub: 'All approved revenue ÷ spend' },
    { label: 'Front-end revenue', value: money(k.frontend_revenue_dkk), sub: 'Approved within 24 h of the lead' },
    { label: 'Email drip revenue', value: money(k.drip_revenue_dkk), sub: 'Approved day 1–90, to date' },
    { label: 'Pending commission', value: money(k.pending_revenue_dkk), sub: 'Reported, not yet approved' },
    { label: 'Net margin', value: money(k.net_margin_dkk), sub: k.projected_d30_revenue_dkk != null ? `Projected D30 revenue ${money(k.projected_d30_revenue_dkk)}` : 'Approved revenue − spend' },
  ];
}

function sortRows(rows: MetaDashboardRow[], key: SortKey, desc: boolean) {
  const sorted = [...rows].sort((a, b) => {
    if (key === 'name') return a.name.localeCompare(b.name, 'da');
    const av = a[key] ?? Number.NEGATIVE_INFINITY;
    const bv = b[key] ?? Number.NEGATIVE_INFINITY;
    return (av as number) - (bv as number);
  });
  return desc ? sorted.reverse() : sorted;
}

const COLUMNS: { key: SortKey; label: string; title: string }[] = [
  { key: 'spend_dkk', label: 'Spend', title: 'Meta spend in the selected dates, DKK' },
  { key: 'leads', label: 'Leads', title: 'Our leads (web form via fbclid/ad ids, plus Instant Form)' },
  { key: 'cpl_dkk', label: 'CPL', title: 'Spend divided by our leads' },
  { key: 'rev_d0', label: 'D0 revenue', title: 'Approved commission within 24 hours of the lead' },
  { key: 'immediate_roas', label: 'Immediate ROAS', title: 'D0 revenue divided by spend' },
  { key: 'rev_d30', label: 'D30 revenue', title: 'Approved commission within 30 days of the lead, to date' },
  { key: 'ltv30_roas', label: '30-day LTV ROAS', title: 'D30 revenue divided by spend; projection uses matured cohorts' },
  { key: 'rev_pending', label: 'Pending', title: 'Commission reported but not yet approved' },
];

function synced(value: string | null | undefined) {
  if (!value) return 'never';
  return new Date(value).toLocaleString('da-DK', { timeZone: TZ, dateStyle: 'short', timeStyle: 'short' });
}

function HealthStrip({ data }: { data: MetaDashboardResponse }) {
  const capi = data.health.capi_24h;
  const sent = (capi.by_status.sent ?? 0) + (capi.by_status.test_sent ?? 0);
  const dryRun = capi.by_status.dry_run ?? 0;
  const problems = capi.auth_errors > 0 || (capi.error_rate ?? 0) > 0.02 || capi.backlog_over_60m > 0;
  const Icon = problems ? TriangleAlert : Info;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-gray-200 px-4 py-2 text-xs text-gray-600" style={{ backgroundColor: MINT }}>
      <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
        <Icon size={14} color={problems ? STATUS.critical : PINE} aria-hidden="true" />
        Conversions API, last 24 h
      </span>
      <span>sent {count(sent)}{dryRun ? ` · dry run ${count(dryRun)}` : ''}</span>
      <span>failed {count(capi.by_status.failed ?? 0)}{capi.error_rate != null ? ` (${pctFormat.format(capi.error_rate)})` : ''}</span>
      <span>email/phone/lead id on {capi.strong_match_share != null ? pctFormat.format(capi.strong_match_share) : '–'} of sent events</span>
      {capi.backlog_over_60m > 0 && <span>{count(capi.backlog_over_60m)} waiting over 60 min</span>}
      {capi.auth_errors > 0 && <span>{count(capi.auth_errors)} token errors</span>}
      <span className="ml-auto">Insights synced {synced(data.health.synced_at.insights_sync)}</span>
    </div>
  );
}

export default function SimplyloanMetaDashboard() {
  const [preset, setPreset] = useState<Preset>('30d');
  const [custom, setCustom] = useState(() => presetRange('30d'));
  const [geo, setGeo] = useState<MetaGeo>('all');
  const [level, setLevel] = useState<MetaLevel>('adset');
  const [sortKey, setSortKey] = useState<SortKey>('spend_dkk');
  const [sortDesc, setSortDesc] = useState(true);

  const range = preset === 'custom' ? custom : presetRange(preset);
  const query = useQuery({
    queryKey: ['simplyloan-meta-dashboard', geo, level, range.from, range.to],
    queryFn: ({ signal }) => fetchMetaDashboard({ geo, level, from: range.from, to: range.to }, signal),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });
  const data = query.data;
  const rows = useMemo(() => (data ? sortRows(data.rows, sortKey, sortDesc) : []), [data, sortKey, sortDesc]);

  const onSort = (key: SortKey) => {
    if (key === sortKey) setSortDesc(!sortDesc);
    else {
      setSortKey(key);
      setSortDesc(key !== 'name' && key !== 'cpl_dkk');
    }
  };

  const levelLabel = LEVELS.find((l) => l.id === level)?.label ?? 'Ad set';

  return (
    <MainLayout>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: PINE }}>Meta Ads · SimplyLoan</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Revenue follows the leads each ad bought: D0 is the first 24 hours, later days come from email drips. All amounts in DKK.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {data && <p className="text-xs text-gray-400">Updated {synced(data.generated_at)}</p>}
            <Link to="/simplyloan/ltv" className="rounded-lg border border-emerald-700 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-900 hover:bg-emerald-100">
              24 Mdr. Kohorte & Email LTV
            </Link>
            <Link to="/simplyloan/meta/campaigns" className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
              Campaign builder
            </Link>
          </div>
        </div>

        {(geo === 'all' || geo === 'se') && <Rolling72hGovernanceCard />}

        {/* One filter row scoping everything below */}
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            label="Date range"
            value={preset}
            onChange={(p) => setPreset(p)}
            options={[{ id: '7d', label: 'Last 7 days', short: '7 d' }, { id: '30d', label: 'Last 30 days', short: '30 d' },
                      { id: '90d', label: 'Last 90 days', short: '90 d' }, { id: 'custom', label: 'Custom' }]}
          />
          {preset === 'custom' && (
            <div className="flex items-center gap-1.5">
              <input
                type="date" aria-label="From" value={custom.from} max={custom.to}
                onChange={(e) => e.target.value && setCustom({ ...custom, from: e.target.value })}
                className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm"
              />
              <span className="text-sm text-gray-400">→</span>
              <input
                type="date" aria-label="To" value={custom.to} min={custom.from}
                onChange={(e) => e.target.value && setCustom({ ...custom, to: e.target.value })}
                className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm"
              />
            </div>
          )}
          <Segmented label="Market" value={geo} onChange={setGeo} options={GEOS} />
          <Segmented label="Level" value={level} onChange={setLevel} options={LEVELS} />
          <button
            type="button"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
            style={{ backgroundColor: ORANGE }}
          >
            <RefreshCw size={14} className={query.isFetching ? 'animate-spin' : ''} aria-hidden="true" />
            Refresh
          </button>
        </div>

        {query.isError && (
          <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
            <TriangleAlert size={16} color={STATUS.critical} className="mt-0.5 shrink-0" aria-hidden="true" />
            <div>
              <p className="font-medium">Could not load the Meta dashboard.</p>
              <p className="text-gray-500">{(query.error as Error).message}</p>
            </div>
          </div>
        )}

        {!data && query.isPending && (
          <div className="flex h-48 items-center justify-center text-sm text-gray-400">Loading Meta performance…</div>
        )}

        {data && (
          <div className={`space-y-5 transition-opacity ${query.isFetching ? 'opacity-60' : 'opacity-100'}`}>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {kpiTiles(data.kpis).map((t) => <KpiTile key={t.label} {...t} />)}
            </div>

            <HealthStrip data={data} />

            {data.kpis.fx_missing && (
              <p className="flex items-center gap-1.5 text-xs text-gray-600">
                <TriangleAlert size={14} color={STATUS.warning} aria-hidden="true" />
                Some spend has no exchange rate yet and counts as 0 DKK. The next Insights sync fills it in.
              </p>
            )}

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <h2 className="text-sm font-semibold" style={{ color: PINE }}>{levelLabel} performance</h2>
                <p className="text-xs text-gray-500">{count(rows.length)} rows · {data.filters.from} to {data.filters.to}</p>
              </div>
              {rows.length === 0 ? (
                <div className="px-4 py-12 text-center text-sm text-gray-500">
                  No Meta spend or Meta-acquired leads in this range.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead style={{ backgroundColor: MINT }}>
                      <tr className="text-left text-xs font-medium text-gray-600">
                        <th scope="col" className="px-4 py-2.5">
                          <button type="button" onClick={() => onSort('name')} className="inline-flex items-center gap-1">
                            {levelLabel}
                            {sortKey === 'name' ? (sortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} className="text-gray-300" />}
                          </button>
                        </th>
                        {COLUMNS.map((c) => (
                          <th key={c.key} scope="col" className="whitespace-nowrap px-3 py-2.5 text-right" title={c.title}
                              aria-sort={sortKey === c.key ? (sortDesc ? 'descending' : 'ascending') : 'none'}>
                            <button type="button" onClick={() => onSort(c.key)} className="inline-flex items-center gap-1">
                              {c.label}
                              {sortKey === c.key ? (sortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} className="text-gray-300" />}
                            </button>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 tabular-nums">
                      {rows.map((r) => (
                        <tr key={r.key ?? 'unattributed'} className="hover:bg-gray-50">
                          <td className="max-w-[22rem] px-4 py-2.5">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="truncate font-medium text-gray-900" title={r.name}>{r.name}</span>
                              <StatusPill status={r.status} />
                              {r.geo && <span className="text-[11px] font-medium uppercase text-gray-400">{r.geo}</span>}
                            </div>
                            {level !== 'campaign' && r.campaign_name && (
                              <p className="truncate text-xs text-gray-400" title={r.campaign_name}>
                                {level === 'ad' && r.adset_name ? `${r.campaign_name} › ${r.adset_name}` : r.campaign_name}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-900">{money(r.spend_dkk)}</td>
                          <td className="px-3 py-2.5 text-right text-gray-900">
                            {count(r.leads)}
                            {r.lead_ad_leads > 0 && <span className="block text-[11px] text-gray-400">{count(r.lead_ad_leads)} form</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-900">{money(r.cpl_dkk)}</td>
                          <td className="px-3 py-2.5 text-right text-gray-900">{money(r.rev_d0)}</td>
                          <td className="px-3 py-2.5 text-right text-gray-900">{roas(r.immediate_roas)}</td>
                          <td className="px-3 py-2.5 text-right text-gray-900">
                            {money(r.rev_d30)}
                            {r.leads > 0 && (
                              <span className="block text-[11px] text-gray-400">{count(r.leads_mature_d30)}/{count(r.leads)} leads 30 d old</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-900">
                            {roas(r.ltv30_roas)}
                            {r.projected_ltv30_roas != null && r.leads_mature_d30 < r.leads && (
                              <span className="block text-[11px] text-gray-400">projected {roas(r.projected_ltv30_roas)}</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-500">{money(r.rev_pending)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t border-gray-200 font-medium tabular-nums" style={{ backgroundColor: MINT }}>
                      <tr>
                        <td className="px-4 py-2.5 text-gray-700">Total</td>
                        <td className="px-3 py-2.5 text-right text-gray-900">{money(data.kpis.spend_dkk)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-900">{count(data.kpis.leads)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-900">{money(data.kpis.cpl_dkk)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-900">{money(data.kpis.frontend_revenue_dkk)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-900">
                          {roas(data.kpis.spend_dkk ? data.kpis.frontend_revenue_dkk / data.kpis.spend_dkk : null)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-gray-900">{money(rows.reduce((s, r) => s + r.rev_d30, 0))}</td>
                        <td className="px-3 py-2.5 text-right text-gray-900">
                          {roas(data.kpis.spend_dkk ? rows.reduce((s, r) => s + r.rev_d30, 0) / data.kpis.spend_dkk : null)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-gray-500">{money(data.kpis.pending_revenue_dkk)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
